import { useState } from 'react';
import { ExerciseStep } from '@codey2/codey-api-types/models';

export interface GeneratedExercise {
  title: string;
  introduction: string;
  platform: string;
  steps: ExerciseStep[];
}

export function useExerciseGenerator() {
  const [error, setError] = useState<string | null>(null);

  const generatePrompt = (description: string): string => {
    return buildPrompt(description);
  };

  const parseExerciseJSON = (jsonText: string): GeneratedExercise | null => {
    setError(null);

    try {
      // Extract JSON from the response (might be wrapped in markdown code blocks)
      const jsonMatch = jsonText.match(/```json\n([\s\S]*?)\n```/) || jsonText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No valid JSON found in response');
      }

      const cleanedJson = jsonMatch[1] || jsonMatch[0];
      const exercise = JSON.parse(cleanedJson) as GeneratedExercise;

      // Validate the exercise structure
      if (!exercise.title || !exercise.introduction || !exercise.platform || !exercise.steps || exercise.steps.length === 0) {
        throw new Error('Generated exercise is missing required fields');
      }

      return exercise;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to parse exercise JSON';
      setError(errorMessage);
      return null;
    }
  };

  return {
    generatePrompt,
    parseExerciseJSON,
    error,
  };
}

function buildPrompt(description: string): string {
  return `You are an expert educational content creator for the Codey platform, which teaches block-based programming to children.

Generate a complete coding exercise based on this description:
${description}

The exercise MUST be returned as valid JSON matching this exact structure:
{
  "title": "string",
  "introduction": "string (markdown format, 2-3 paragraphs explaining the exercise goal)",
  "platform": "SCRATCH" | "MICROBIT" | "COSPACES",
  "steps": [
    {
      "name": "string (step title)",
      "content": "string (markdown content with code blocks)"
    }
  ]
}

## CODE BLOCK SYNTAX RULES

When creating exercise steps, use the custom code block syntax for visual programming blocks. This syntax creates Scratch/Blockly-style visual blocks.

### Basic Structure
All code blocks must be wrapped in a code fence starting with @code and ending with @code-end:

\`\`\`
@code
[blocks go here]
@code-end
\`\`\`

### Color Definitions (Optional)
Define custom colors at the start:
@color[key="motion";value="#4C97FF"]
@color[key="control";value="#FFAB19"]

### Platform Default Colors

**SCRATCH:**
- motion: #4C97FF (blue)
- looks: #9966FF (purple)
- sound: #CF63CF (pink)
- events: #FFD500 (yellow)
- control: #FFAB19 (orange)
- sensing: #5CB1D6 (cyan)
- operators: #59C059 (green)
- variables: #FF8C1A (orange)
- myblocks: #FF6680 (red)

**MICROBIT:**
- basic: #19B5E7
- input: #D400D4
- music: #e63022
- led: #5c2d91
- radio: #e3008c
- loops: #00aa00
- logic: #00a4a6
- variables: #dc143c
- math: #9400d3

**COSPACES:**
- scene: #4A90E2
- objects: #7B68EE
- animation: #FF69B4
- events: #FFD700
- control: #FF8C00
- logic: #32CD32
- math: #9370DB
- variables: #FF6347

### Code Blocks
Create colored blocks:

@block[color=motion]
move (10) steps

@block[color=events]
when [green flag] clicked

### Control Structures
Use @repeat, @if, @elseif, @else, @end:

@repeat
(10)
  @block[color=motion]
  move forward
@end

@if
<(x-position) [>] (0)>
  @block[color=looks]
  say [Hello!]
@end

### Value Slot Syntax
- Circle values (numbers/variables): (10), ($myVariable)
- Diamond conditions (boolean): <touching [edge]?>
- Hexagon operators: [>], [and], [left]

### Nested Values with Colors
Use #colorKey: prefix:

<(#sensing:mouse-x) [>] (0)>
set x to (#motion:x-position)

### Complete Example

\`\`\`
@code
@block[color=events]
when [green flag] clicked

@block[color=motion]
go to x: (0) y: (0)

@repeat
[forever]
  @block[color=motion]
  move (10) steps

  @if
  <#sensing:touching [edge]?>
    @block[color=motion]
    turn ↻ (180) degrees
  @end
@end
@code-end
\`\`\`

## REQUIREMENTS

1. **Introduction**: Write 2-3 engaging paragraphs in markdown explaining what students will build and learn
2. **Steps**: Break the exercise into 5-8 clear steps, each with:
   - A descriptive name (e.g., "Setup the sprite", "Add movement controls")
   - Content that mixes regular markdown explanation with code blocks
   - Progressive difficulty (start simple, build complexity)
3. **Code blocks**: Use the custom syntax above for all programming instructions
4. **Age-appropriate**: Match language and complexity to the specified age range
5. **Complete solution**: By the end of all steps, students should have a working program
6. **language**: Use clear, concise language suitable for children and exclusively use Dutch.

Return ONLY the JSON object, no additional explanation.`;
}
